import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128, pattern: "^\\S+$" });
const PositiveQuantitySchema = Type.Integer({ minimum: 1, maximum: 999_999 });
const CanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern:
    "^[0-9]{4}-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])T([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]\\.[0-9]{3}Z$",
});
const NoteSchema = Type.String({
  minLength: 2,
  maxLength: 300,
  pattern: "^\\S(?:[\\s\\S]*\\S)?$",
});

export const CustomerStorageStatusSchema = Type.Union([
  Type.Literal("ACTIVE"),
  Type.Literal("COLLECTED"),
  Type.Literal("EXCEPTION_PENDING"),
  Type.Literal("EXCEPTION_RESOLVED"),
]);
export type CustomerStorageStatus = Static<typeof CustomerStorageStatusSchema>;

export const CustomerStorageExceptionTypeSchema = Type.Union([
  Type.Literal("LOST"),
  Type.Literal("DAMAGED"),
  Type.Literal("DISPOSAL"),
]);
export type CustomerStorageExceptionType = Static<typeof CustomerStorageExceptionTypeSchema>;

export const CustomerStorageHistoryActionSchema = Type.Union([
  Type.Literal("STORED"),
  Type.Literal("PICKED_UP"),
  Type.Literal("EXCEPTION_REPORTED"),
  Type.Literal("EXCEPTION_CONFIRMED"),
  Type.Literal("EXCEPTION_REJECTED"),
]);
export type CustomerStorageHistoryAction = Static<typeof CustomerStorageHistoryActionSchema>;

export const CustomerStorageHistoryEntrySchema = Type.Object(
  {
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    action: CustomerStorageHistoryActionSchema,
    quantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
    remainingQuantityAfter: Type.Integer({ minimum: 0, maximum: 999_999 }),
    note: NoteSchema,
    operatorDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    occurredAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerStorageHistoryEntry = Static<typeof CustomerStorageHistoryEntrySchema>;

export const CustomerStorageExceptionSnapshotSchema = Type.Object(
  {
    exceptionType: CustomerStorageExceptionTypeSchema,
    note: NoteSchema,
    status: Type.Union([
      Type.Literal("PENDING"),
      Type.Literal("CONFIRMED"),
      Type.Literal("REJECTED"),
    ]),
    workItemId: StableIdSchema,
    reportedAt: CanonicalDateTimeSchema,
    resolvedAt: Type.Union([CanonicalDateTimeSchema, Type.Null()]),
    resolutionReason: Type.Union([NoteSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type CustomerStorageExceptionSnapshot = Static<
  typeof CustomerStorageExceptionSnapshotSchema
>;

export const CustomerStorageRecordSchema = Type.Object(
  {
    storageNumber: Type.String({ pattern: "^JC-[0-9]{8}-[0-9A-F]{12}$" }),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    memberVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    sourceOrderNumber: Type.String({ minLength: 1, maxLength: 64 }),
    sourceOrderVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    sourceOrderItemId: StableIdSchema,
    skuId: StableIdSchema,
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    productName: Type.String({ minLength: 1, maxLength: 120 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    originalQuantity: PositiveQuantitySchema,
    remainingQuantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
    status: CustomerStorageStatusSchema,
    expired: Type.Boolean(),
    expiresAt: CanonicalDateTimeSchema,
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
    latestException: Type.Union([CustomerStorageExceptionSnapshotSchema, Type.Null()]),
    history: Type.Array(CustomerStorageHistoryEntrySchema, { minItems: 1, maxItems: 10_000 }),
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type CustomerStorageRecord = Static<typeof CustomerStorageRecordSchema>;

export const CustomerStorageSourceCandidateSchema = Type.Object(
  {
    sourceOrderId: StableIdSchema,
    sourceOrderNumber: Type.String({ minLength: 1, maxLength: 64 }),
    sourceOrderVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    memberId: StableIdSchema,
    memberNumber: Type.String({ minLength: 1, maxLength: 64 }),
    memberDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    memberVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    items: Type.Array(
      Type.Object(
        {
          sourceOrderItemId: StableIdSchema,
          skuId: StableIdSchema,
          productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
          productName: Type.String({ minLength: 1, maxLength: 120 }),
          unit: Type.String({ minLength: 1, maxLength: 16 }),
          purchasedQuantity: PositiveQuantitySchema,
          returnedQuantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
          alreadyStoredQuantity: Type.Integer({ minimum: 0, maximum: 999_999 }),
          availableQuantity: PositiveQuantitySchema,
        },
        { additionalProperties: false },
      ),
      { minItems: 1, maxItems: 1_000 },
    ),
  },
  { additionalProperties: false },
);
export type CustomerStorageSourceCandidate = Static<typeof CustomerStorageSourceCandidateSchema>;

export const CustomerStorageSourceCandidateListSchema = Type.Array(
  CustomerStorageSourceCandidateSchema,
  { maxItems: 100 },
);

export function isCustomerStorageSourceCandidateList(
  value: unknown,
): value is CustomerStorageSourceCandidate[] {
  if (!Value.Check(CustomerStorageSourceCandidateListSchema, value)) return false;
  const orderIds = new Set<string>();
  const itemIds = new Set<string>();
  for (const candidate of value) {
    if (orderIds.has(candidate.sourceOrderId)) return false;
    orderIds.add(candidate.sourceOrderId);
    for (const item of candidate.items) {
      if (
        itemIds.has(item.sourceOrderItemId) ||
        item.returnedQuantity > item.purchasedQuantity ||
        item.alreadyStoredQuantity > item.purchasedQuantity - item.returnedQuantity ||
        item.availableQuantity !==
          item.purchasedQuantity - item.returnedQuantity - item.alreadyStoredQuantity
      ) {
        return false;
      }
      itemIds.add(item.sourceOrderItemId);
    }
  }
  return true;
}

export const CustomerStorageCreateRequestSchema = Type.Object(
  {
    memberId: StableIdSchema,
    sourceOrderId: StableIdSchema,
    sourceOrderItemId: StableIdSchema,
    quantity: PositiveQuantitySchema,
    expiresAt: CanonicalDateTimeSchema,
    note: NoteSchema,
    expectedOrderVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedMemberVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedProductVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerStorageCreateRequest = Static<typeof CustomerStorageCreateRequestSchema>;

export const CustomerStoragePickupRequestSchema = Type.Object(
  {
    quantity: PositiveQuantitySchema,
    note: NoteSchema,
    expectedVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerStoragePickupRequest = Static<typeof CustomerStoragePickupRequestSchema>;

export const CustomerStorageExceptionReportRequestSchema = Type.Object(
  {
    exceptionType: CustomerStorageExceptionTypeSchema,
    note: NoteSchema,
    expectedVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerStorageExceptionReportRequest = Static<
  typeof CustomerStorageExceptionReportRequestSchema
>;

export const CustomerStorageExceptionDecisionRequestSchema = Type.Object(
  {
    decision: Type.Union([Type.Literal("CONFIRM"), Type.Literal("REJECT")]),
    reason: NoteSchema,
    expectedStorageVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedWorkItemVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CustomerStorageExceptionDecisionRequest = Static<
  typeof CustomerStorageExceptionDecisionRequestSchema
>;

export const CustomerStorageOperationResultSchema = Type.Object(
  {
    storage: CustomerStorageRecordSchema,
    workItemId: Type.Union([StableIdSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type CustomerStorageOperationResult = Static<typeof CustomerStorageOperationResultSchema>;

function isCanonicalTimestamp(value: string): boolean {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) && new Date(parsed).toISOString() === value;
}

export function isCustomerStorageCreateRequest(
  value: unknown,
): value is CustomerStorageCreateRequest {
  if (!Value.Check(CustomerStorageCreateRequestSchema, value)) return false;
  const request = value as CustomerStorageCreateRequest;
  return isCanonicalTimestamp(request.expiresAt) && request.note.trim() === request.note;
}

export function isCustomerStoragePickupRequest(
  value: unknown,
): value is CustomerStoragePickupRequest {
  return (
    Value.Check(CustomerStoragePickupRequestSchema, value) &&
    (value as CustomerStoragePickupRequest).note.trim() ===
      (value as CustomerStoragePickupRequest).note
  );
}

export function isCustomerStorageExceptionReportRequest(
  value: unknown,
): value is CustomerStorageExceptionReportRequest {
  return (
    Value.Check(CustomerStorageExceptionReportRequestSchema, value) &&
    (value as CustomerStorageExceptionReportRequest).note.trim() ===
      (value as CustomerStorageExceptionReportRequest).note
  );
}

export function isCustomerStorageExceptionDecisionRequest(
  value: unknown,
): value is CustomerStorageExceptionDecisionRequest {
  return (
    Value.Check(CustomerStorageExceptionDecisionRequestSchema, value) &&
    (value as CustomerStorageExceptionDecisionRequest).reason.trim() ===
      (value as CustomerStorageExceptionDecisionRequest).reason
  );
}

export function isCustomerStorageRecord(value: unknown): value is CustomerStorageRecord {
  if (!Value.Check(CustomerStorageRecordSchema, value)) return false;
  const record = value as CustomerStorageRecord;
  if (
    record.remainingQuantity > record.originalQuantity ||
    record.history.length !== record.version ||
    !isCanonicalTimestamp(record.createdAt) ||
    !isCanonicalTimestamp(record.updatedAt) ||
    !isCanonicalTimestamp(record.expiresAt)
  ) {
    return false;
  }
  let previousRemaining = record.originalQuantity;
  for (const [index, entry] of record.history.entries()) {
    if (
      entry.version !== index + 1 ||
      !isCanonicalTimestamp(entry.occurredAt) ||
      entry.note.trim() !== entry.note ||
      entry.operatorDisplayName.trim() !== entry.operatorDisplayName
    ) {
      return false;
    }
    if (index === 0) {
      if (
        entry.action !== "STORED" ||
        entry.quantity !== record.originalQuantity ||
        entry.remainingQuantityAfter !== record.originalQuantity
      ) {
        return false;
      }
    } else if (entry.action === "PICKED_UP" || entry.action === "EXCEPTION_CONFIRMED") {
      if (entry.quantity < 1 || entry.quantity > previousRemaining) return false;
      previousRemaining -= entry.quantity;
      if (entry.remainingQuantityAfter !== previousRemaining) return false;
    } else if (entry.quantity !== 0 || entry.remainingQuantityAfter !== previousRemaining) {
      return false;
    }
    previousRemaining = entry.remainingQuantityAfter;
  }
  if (previousRemaining !== record.remainingQuantity) return false;
  if (
    (record.status === "ACTIVE" &&
      (record.remainingQuantity === 0 || record.latestException?.status === "PENDING")) ||
    (record.status === "COLLECTED" && record.remainingQuantity !== 0) ||
    (record.status === "EXCEPTION_PENDING" && record.latestException?.status !== "PENDING") ||
    (record.status === "EXCEPTION_RESOLVED" &&
      (record.remainingQuantity !== 0 || record.latestException?.status !== "CONFIRMED"))
  ) {
    return false;
  }
  if (record.latestException) {
    if (
      !isCanonicalTimestamp(record.latestException.reportedAt) ||
      (record.latestException.status === "PENDING") !==
        (record.latestException.resolvedAt === null &&
          record.latestException.resolutionReason === null) ||
      (record.latestException.status !== "PENDING" &&
        (record.latestException.resolvedAt === null ||
          record.latestException.resolutionReason === null))
    ) {
      return false;
    }
  }
  return true;
}

export function isCustomerStorageOperationResult(
  value: unknown,
): value is CustomerStorageOperationResult {
  return (
    Value.Check(CustomerStorageOperationResultSchema, value) &&
    isCustomerStorageRecord((value as CustomerStorageOperationResult).storage)
  );
}
